<template lang="pug">
#activities-location-sidebar-members-tab
  AppInputSearch
    AppInput(
      :debounce="0"
      type="search"
      placeholder="Search members"
      @input="query = $event"
    )
  ActivitiesLocationSortBy(
    :value="membersGroupBy"
    :options="membersGroupByOptions"
    @input="membersGroupBy = $event"
  )
  .activities-location-sidebar-loading(v-if="usersLoading && !showError")
    .spinner
    .copy The members list is loading.
  .activities-location-sidebar-error(v-else-if="showError")
    | {{ showError }}
    a.clickable.text-underline(@click="loadUsersResource") Please try again.
  template(v-if="!showError && !usersLoading")
    template(v-if="anyMembersHaveRecentData")
      template(v-if="usersByPartition.tracked.length")
        AppSelectList.active-members(v-if="trackedMembers.length")
          ActivitiesLocationSidebarMemberItem(
            v-for="user in trackedMembers"
            ref="memberItem"
            :key="user.id"
            :data-member="`member_${user.id}`"
            :current-address="membersState[`member_${user.id}`]?.address"
            :address-timestamp="membersState[`member_${user.id}`]?.lastAddressTimestamp"
            :user="user"
            :is-past="isPastView"
            :selected="isSelectedMember(user.id)"
            :active-route="isSelectedMember(user.id) && showingRoutes"
            :active-info="isSelectedMember(user.id) && showingInfoWindow"
            @click="onSelectMember(user)"
            @view-info="showInfoWindow(true)"
            @hide-info="showInfoWindow(false)"
            @view-route="showRoutes(true)"
            @hide-route="showRoutes(false)"
          )
        .text-lighter.no-active-members(v-else) {{ noMembersText(usersByPartition.tracked, trackedMembers) }}
      template(v-else)
        .empty-data.d-flex.align-items-center.justify-content-center.flex-column.my-40
          img.mb-20(
            :width="140"
            :src="activitiesEmtpySidebarGPSIllustration"
            :alt="topMembersEmptyStateText"
          )
          p.hint.small.m-0 {{ topMembersEmptyStateText }}

      template(v-if="usersByPartition.notTracked.length")
        .activities-location-sidebar__divider.text-center
          span.hint.m-0 Members with no locations recorded

        AppSelectList.inactive-members(v-if="notTrackedMembers.length")
          ActivitiesLocationSidebarMemberItem(
            v-for="user in notTrackedMembers"
            :key="user.id"
            :user="user"
            :is-past="isPastView"
            :selected="isSelectedMember(user.id)"
            @click="onSelectMember(user)"
            @change-date="onMemberChangeDate(user, $event)"
          )
        .text-lighter.no-inactive-members(v-else) {{ noMembersText(usersByPartition.notTracked, notTrackedMembers) }}
    template(v-else)
      .empty-data.d-flex.align-items-center.justify-content-center.flex-column.my-40
        img.mb-20(
          :width="140"
          :src="activitiesEmtpySidebarGPSIllustration"
          alt="No members have recent locations"
        )
        p.hint.small.m-0 No members have recent locations
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, sortBy, lowerCase } from 'lodash-es';
import { isMobile, GeolocateMixin } from '~/mixins';
import { LOCATION_VIEW } from '~/app_constants';
import { ACTIVITIES_SIDEBAR_NO_GPS } from '~/rails_assets';

export default {
  name: 'ActivitiesLocationSidebarMembersTab',
  mixins: [isMobile, GeolocateMixin],
  data: () => ({
    query: '',
    browserPageActive: true,
    membersObserver: null,
    membersState: {},
    memberLabel: 'member',
    membersGroupBy: { id: 'lastActivity', text: 'Last worked' },
    membersGroupByOptions: Object.freeze([
      { id: 'lastActivity', text: 'Last worked' },
      { id: 'alpha', text: 'Alphabetically' },
    ]),
    activitiesEmtpySidebarGPSIllustration: ACTIVITIES_SIDEBAR_NO_GPS,
  }),
  computed: {
    ...mapState(['sites', 'selectedMember', 'showingRoutes', 'showingInfoWindow']),
    ...mapGetters(['isPastView', 'usersByPartition', 'usersLoading', 'apiErrors', 'activeTimeZoneName']),
    trackedMembers() {
      return this.filterMembers(this.usersByPartition.tracked);
    },
    notTrackedMembers() {
      return this.filterMembers(this.usersByPartition.notTracked);
    },
    anyMembersHaveRecentData() {
      const { tracked, notTracked } = this.usersByPartition;

      return tracked.length || notTracked.length;
    },
    topMembersEmptyStateText() {
      return `No members have locations for ${this.isPastView ? 'this day' : 'today'}`;
    },
    showError() {
      return this.apiErrors && this.apiErrors.users && this.apiErrors.users.message;
    },
  },
  watch: {
    'usersByPartition.tracked': {
      immediate: false,
      async handler() {
        setTimeout(() => {
          this.destroyObserver();
          this.attachObserver();
        }, 0);
      },
    },
  },
  async mounted() {
    this.onBrowserVisibilityChange = this.onBrowserVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.onBrowserVisibilityChange);
  },
  beforeDestroy() {
    this.destroyObserver();
    document.removeEventListener('visibilitychange', this.onBrowserVisibilityChange);
  },
  methods: {
    ...mapActions([
      'setDate',
      'showRoutes',
      'selectMember',
      'showInfoWindow',
      'clearSelected',
      'setCurrentView',
      'loadUsersResource',
    ]),
    onBrowserVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // If the document is visible, make sure that we track the items
        // and request their address if necessary.
        this.destroyObserver();
        this.attachObserver();
      }

      this.browserPageActive = document.visibilityState === 'visible';
    },
    destroyObserver() {
      this.membersObserver?.disconnect();
      this.membersObserver = null;
    },
    attachObserver() {
      if (this.isPastView || !this.trackedMembers.length) {
        return;
      }

      this.membersObserver = new IntersectionObserver(this.handleItersect, { root: null, threshold: 0.5 });

      for (const memberItem of this.$refs.memberItem || []) {
        const memberKey = memberItem.$el.dataset.member;

        if (!this.membersState[memberKey]) {
          Vue.set(this.membersState, memberKey, {
            ref: memberItem,
            address: null,
            lastAddressTimestamp: null,
          });
        } else {
          // If we already have the key in the `membersState` we only want to update the component
          // reference since the child component will have "re-mounted".
          Vue.set(this.membersState[memberKey], 'ref', memberItem);
        }

        this.membersObserver.observe(memberItem.$el);
      }
    },
    handleItersect(entries) {
      entries.forEach((entry) => {
        const memberKey = entry.target.dataset.member;

        if (entry.isIntersecting) {
          this.getUserLocationAddress(memberKey);
        }
      });
    },
    /**
     * In order to reduce the number of requests to Google, we now only request the address for users visible in the sidebar and not hidden by the scroll.
     * We also only do so if the following are true:
     *   - Their last location recorded_at is younger than 15' “ago”
     *   - The location is not from any known jobsite
     *   - The last request to the Geocoding service is older than 10' “ago”
     *   - The browser page is visible/active (not idle/in the background)
     */
    async getUserLocationAddress(memberKey) {
      const memberState = this.membersState[memberKey];
      // Get the user prop from the component
      const user = memberState.ref.user;
      const { activeNow, trackedLocation, location } = user;
      const lastAddressTimestamp = memberState.lastAddressTimestamp;

      if (!location || !trackedLocation || this.isPast) {
        return;
      }

      Vue.set(memberState, 'lastAddressTimestamp', null);

      const { site_id: siteId, latitude, longitude } = location;

      if (typeof siteId === 'number') {
        const { address } = this.sites[siteId] || {};

        Vue.set(memberState, 'address', address ? `At ${address}` : null);

        return;
      }

      if (
        // Avoid any requests when the browser is not active
        !this.browserPageActive ||
        // We use the `activeNow` field in the `user`'s object since that already tracks if
        // the location is older than 15 minutes.
        !activeNow ||
        // If the location is not older than 15 minutes, then we make sure that we only
        // request the address every 10 minutes.
        (lastAddressTimestamp && moment().diff(lastAddressTimestamp, 'minutes') < 10)
      ) {
        return;
      }

      const position = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
      const newTimestamp = this.activeTimeZoneName ? moment.tz(moment(), this.activeTimeZoneName) : moment();

      Vue.set(memberState, 'lastAddressTimestamp', newTimestamp);

      this.getClientAddress(position)
        .then((address) => {
          Vue.set(memberState, 'address', address ? `Near ${address.formatted_address}` : 'Address not found');
        })
        .catch(() => {
          // Clear the timestamp after failed request, so that we can try again
          // for this user on the next live update of the page.
          Vue.set(memberState, 'lastAddressTimestamp', null);
          Vue.set(memberState, 'address', 'Address not found');
        });
    },
    filterMembers(members = []) {
      const query = lowerCase(this.query);
      const filteredMembers = !this.query
        ? members
        : members.filter((member) => lowerCase(member.name).includes(query));

      return sortBy(filteredMembers, [
        (member) => (this.membersGroupBy.id === 'alpha' ? member.name : get(member, 'lastActivity.client_activity_at')),
      ]);
    },
    noMembersText(originalCollection = [], filteredCollection = []) {
      const pluralizeLabel = this.$options.filters.pluralize(2, this.memberLabel);

      return originalCollection.length && !filteredCollection.length && this.query
        ? `No ${pluralizeLabel} matched this search`
        : '';
    },
    isSelectedMember(userId) {
      return get(this.selectedMember, 'id') === userId;
    },
    commonSelectMember(member, trackedLocation) {
      this.clearSelected();
      this.selectMember(member);
      this.$emit('selected', trackedLocation);
    },
    onSelectMember(member) {
      this.commonSelectMember(member, member.trackedLocation);
    },
    async onMemberChangeDate(member, date) {
      if (!this.isPastView) {
        this.setCurrentView(LOCATION_VIEW.PAST);
      }

      this.setDate(date);
      this.commonSelectMember(member, true);
    },
  },
};
</script>
